import React from 'react';
import type { BillingSummaryData } from '@/api-types';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/auth-context';

const emptyBillingSummary: BillingSummaryData = {
	balance: {
		balance: 0,
		lifetimeCreditsPurchased: 0,
		lifetimeCreditsUsed: 0,
		freeSignupCredits: 0,
		generationCreditCost: 0,
	},
	packs: [],
	transactions: [],
	usageByDay: [],
	stripeConfigured: false,
};

export function useBillingSummary() {
	const { user } = useAuth();
	const accountKey = user?.provider === 'github' ? user.id : null;
	const [summary, setSummary] =
		React.useState<BillingSummaryData>(emptyBillingSummary);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	const refresh = React.useCallback(async () => {
		if (!accountKey) {
			setSummary(emptyBillingSummary);
			setLoading(false);
			setError(null);
			return;
		}

		try {
			setLoading(true);
			setError(null);
			const response = await apiClient.getBillingSummary();
			if (response.success && response.data) {
				setSummary(response.data);
				return;
			}
			setError(response.error?.message || 'Failed to load credits');
		} catch (caughtError) {
			console.error('Error loading billing summary:', caughtError);
			setError('Failed to load credits');
		} finally {
			setLoading(false);
		}
	}, [accountKey]);

	React.useEffect(() => {
		void refresh();
	}, [refresh]);

	return {
		summary,
		loading,
		error,
		refresh,
		isAvailable: user?.provider === 'github',
		walletLabel: user?.username ? `@${user.username}` : 'GitHub wallet',
	};
}
